"use client";
import { useState, type FormEvent } from "react";
import {
  Activity,
  ArrowDownToLine,
  ArrowRight,
  Cable,
  Download,
  KeyRound,
  Monitor,
  Play,
  ShieldCheck,
} from "lucide-react";
import { Button, Dialog, DialogActions, Field } from "../../design-system";
import {
  desktopReleaseSlots,
  type DesktopAppRelease,
} from "./desktop-download-data";
import "./desktop-download.css";
export {
  publicDownloadFlows,
  desktopReleaseSlots,
  unavailableDesktopRelease,
  type DesktopAppRelease,
  type PublicDownloadFlow,
} from "./desktop-download-data";

function ReleaseMetadata({
  release,
  placeholders = false,
}: {
  release: DesktopAppRelease;
  placeholders?: boolean;
}) {
  const version = release.version?.trim().replace(/^v/i, "");
  const size =
    release.size !== null
      ? Math.round(release.size / 1024 / 1024) + " MB"
      : null;
  const published = release.publishedAt?.slice(0, 10).replaceAll("-", ". ");
  return (
    <div className="meta-crew-download-release" aria-label="배포 정보">
      <span>Windows · 64비트</span>
      <span aria-label="버전">
        {version ? "v" + version : placeholders ? "버전 정보" : "버전 —"}
      </span>
      <span aria-label="파일 크기">
        {size ?? (placeholders ? "파일 크기" : "용량 —")}
      </span>
      <span aria-label="배포일">
        {published ? "배포 " + published : placeholders ? "배포일" : "배포일 —"}
      </span>
    </div>
  );
}

/** Shared public install dialog. The caller supplies release data and open state. */
export function InstallGuideDialog({
  open,
  onOpenChange,
  release = desktopReleaseSlots,
  preview = true,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  release?: DesktopAppRelease;
  preview?: boolean;
}) {
  const steps = [
    {
      icon: ArrowDownToLine,
      title: "Worker 내려받기",
      text: "설치할 Windows PC에서 로그인 화면의 ‘Worker 내려받기’를 누르세요.",
      detail: "Windows 10 또는 11 · 64비트",
    },
    {
      icon: Play,
      title: "설치 파일 실행",
      text: "내려받은 설치 파일을 실행하세요. Windows 보호 화면이 나타나면 ‘추가 정보’를 누른 뒤 ‘실행’을 선택하세요.",
      detail: "Windows 보호 화면: 추가 정보 → 실행",
    },
    {
      icon: KeyRound,
      title: "연결 코드 8자리 입력",
      text: "Meta Crew Worker를 열고 관리자에게 받은 연결 코드 8자리를 입력하세요. 고객사에 연결되면 이 PC가 등록됩니다.",
      detail: "연결 코드가 없으면 담당 관리자에게 요청하세요.",
    },
    {
      icon: Cable,
      title: "폰 연결 · USB 디버깅 허용",
      text: "안드로이드 폰을 USB 케이블로 PC에 연결하세요. 개발자 옵션에서 USB 디버깅을 켜고, 폰에 뜨는 ‘USB 디버깅을 허용하시겠습니까?’ 안내를 허용하세요.",
      detail: "Worker에서 디바이스가 준비됨으로 표시되면 완료입니다.",
    },
  ];
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Meta Crew Worker 설치 방법"
      description="Windows PC에 Worker를 설치하고 연결 코드와 USB 디바이스를 연결하는 네 단계 안내입니다."
      size="wide"
      footer={
        <DialogActions
          primary={{ label: "확인", onClick: () => onOpenChange(false) }}
        />
      }
    >
      <div className="meta-crew-install-guide">
        <div className="meta-crew-install-guide-intro">
          <span>
            <Monitor size={24} aria-hidden="true" />
          </span>
          <div>
            <strong>현장 PC와 디바이스를 연결하세요</strong>
            <p>
              로그인과 별개로, 디바이스를 연결할 PC에 한 번만 설치하면 됩니다.
            </p>
          </div>
        </div>
        {release.available ? (
          <ReleaseMetadata release={release} placeholders={preview} />
        ) : (
          <p className="meta-crew-install-guide-unavailable" role="status">
            현재 배포본을 준비하고 있습니다. 배포 후 로그인 화면에서 내려받을 수
            있습니다.
          </p>
        )}
        <ol className="meta-crew-install-guide-steps" aria-label="설치 순서">
          {steps.map(({ icon: Icon, title, text, detail }, index) => (
            <li key={title}>
              <span className="meta-crew-install-guide-number">
                {index + 1}
              </span>
              <div>
                <h3>
                  <Icon size={17} aria-hidden="true" />
                  {title}
                </h3>
                <p>{text}</p>
                <small>{detail}</small>
              </div>
            </li>
          ))}
        </ol>
        <div className="meta-crew-install-guide-requirements">
          <ShieldCheck size={18} aria-hidden="true" />
          <p>
            <strong>준비물</strong>Windows 10/11 64비트 PC · 안드로이드 폰 · USB
            케이블 · 인터넷 연결
          </p>
        </div>
      </div>
    </Dialog>
  );
}

/** Compact installation entry inside the login card, never a second card. */
export function WorkerDownloadAction({
  release,
  preview = false,
  onOpenGuide,
}: {
  release: DesktopAppRelease;
  preview?: boolean;
  onOpenGuide: () => void;
}) {
  const [notice, setNotice] = useState("");
  const safeUrl =
    release.downloadUrl && /^(https?:\/\/|\/(?!\/))/.test(release.downloadUrl)
      ? release.downloadUrl
      : null;
  const showDownload = release.available && (preview || !!safeUrl);
  return (
    <section className="meta-crew-download-entry" aria-label="Worker 설치">
      <div className="meta-crew-download-desktop-action">
        {showDownload ? (
          <>
            {preview ? (
              <Button
                variant="secondary"
                className="meta-crew-download-worker-button"
                onClick={() =>
                  setNotice("시안에서는 설치 파일을 내려받지 않습니다.")
                }
              >
                <Download size={19} aria-hidden="true" />
                <strong>Worker 내려받기</strong>
                <ArrowRight size={16} aria-hidden="true" />
              </Button>
            ) : (
              <a
                className="button button--secondary button--md meta-crew-download-worker-button"
                href={safeUrl!}
                download
              >
                <Download size={19} aria-hidden="true" />
                <strong>Worker 내려받기</strong>
                <ArrowRight size={16} aria-hidden="true" />
              </a>
            )}
            <ReleaseMetadata release={release} placeholders={preview} />
          </>
        ) : (
          <p className="meta-crew-download-unavailable">
            배포본을 준비하고 있습니다.
          </p>
        )}
      </div>
      <div className="meta-crew-download-mobile-note" role="note">
        <Monitor size={19} aria-hidden="true" />
        <div>
          <strong>PC에서 열어 주세요</strong>
          <p>Worker 설치는 Windows PC에서 진행해 주세요.</p>
        </div>
      </div>
      {notice && (
        <p role="status" className="meta-crew-download-notice">
          {notice}
        </p>
      )}
      <Button
        variant="ghost"
        className="meta-crew-download-guide-link"
        onClick={onOpenGuide}
      >
        설치 방법 보기
        <ArrowRight size={14} aria-hidden="true" />
      </Button>
    </section>
  );
}
export type MetaCrewDownloadPageProps = {
  release?: DesktopAppRelease;
  loginScope?: "admin" | "customer";
  preview?: boolean;
  initialGuideOpen?: boolean;
  onLogin?: (credentials: { email: string; password: string }) => Promise<void>;
};
export function MetaCrewDownloadPage({
  release,
  loginScope = "customer",
  preview = release === undefined,
  initialGuideOpen = false,
  onLogin,
}: MetaCrewDownloadPageProps) {
  const [guideOpen, setGuideOpen] = useState(initialGuideOpen);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const currentRelease = release ?? desktopReleaseSlots;
  const login = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setError("");
    setNotice("");
    if (!onLogin) {
      setNotice("로그인 화면 시안입니다. 실제 인증은 연결되지 않았습니다.");
      return;
    }
    setBusy(true);
    try {
      await onLogin({
        email: String(form.get("email")).trim(),
        password: String(form.get("password")),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="meta-crew-download-page">
      <div className="meta-crew-download-login-wrap">
        <div className="meta-crew-download-brand">
          <span>
            <Activity size={23} aria-hidden="true" />
          </span>
          Meta Crew
        </div>
        <section className="meta-crew-download-login-card">
          <span className="meta-crew-download-eyebrow">
            {loginScope === "admin" ? "ADMIN CONSOLE" : "CUSTOMER CONSOLE"}
          </span>
          <h1>로그인</h1>
          <p>
            {loginScope === "admin"
              ? "전체 고객사의 운영 현황을 확인하세요."
              : "우리 고객사의 운영 현황을 확인하세요."}
          </p>
          <form onSubmit={login}>
            <Field
              label="이메일"
              name="email"
              type="email"
              placeholder="name@company.com"
              autoComplete="username"
              required
            />
            <Field
              label="비밀번호"
              name="password"
              type="password"
              placeholder="비밀번호 입력"
              autoComplete="current-password"
              required
            />
            {error && (
              <p className="meta-crew-download-error" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" loading={busy}>
              로그인
              <ArrowRight size={16} aria-hidden="true" />
            </Button>
            {notice && (
              <p role="status" className="meta-crew-download-notice">
                {notice}
              </p>
            )}
            <small>비밀번호를 잊으셨다면 담당자에게 재설정을 요청하세요.</small>
          </form>
          <WorkerDownloadAction
            release={currentRelease}
            preview={preview}
            onOpenGuide={() => setGuideOpen(true)}
          />
        </section>
        <small className="meta-crew-download-footer">
          고객사와 현장을 잇는 운영 플랫폼
        </small>
      </div>
      <InstallGuideDialog
        open={guideOpen}
        onOpenChange={setGuideOpen}
        release={currentRelease}
        preview={preview}
      />
    </main>
  );
}
